import { useMemo } from "react";
import { PieChartItem } from "../views/pie-chart/shared";
import { t } from "i18next";
import { RiskFactor, riskFactorColors, Score } from "@keita/lanepatrol-sdk";
import { useStores } from "../../../shared/stores/RootStore";

const emptyRiskFactor = "null";
const riskFactorsAndEmpty = [...Object.values(RiskFactor), emptyRiskFactor];

export const useHlaScorePieCharts = () => {
  const { filterStore } = useStores();

  const filteredDistance = filterStore.filteredHlaDistance;
  const filteredWays = filterStore.filteredHlaWays;

  return useMemo(() => {
    if (!filteredWays.length) return null;

    const data: Record<keyof Score, PieChartItem[]> = {
      global: [],
      vehicleToBicycle: [],
      bicycleToBicycle: [],
      bicycleToPedestrian: [],
      singleBicycle: [],
    };
    const scoreOverview: Partial<
      Record<keyof Score, Record<RiskFactor | "null", number>>
    > = {};

    // initialize counters
    Object.keys(data).forEach((crashType) => {
      scoreOverview[crashType as keyof Score] = {} as any;

      riskFactorsAndEmpty.forEach((riskFactor) => {
        scoreOverview[crashType as keyof Score]![
          riskFactor as RiskFactor | "null"
        ] = 0;
      });
    });

    filteredWays.forEach((way) => {
      [way.frontFacingLane, way.backFacingLane].forEach((lane) => {
        if (lane == null) return;
        if (!filterStore.filterHlaLane(lane)) return;

        Object.keys(data).forEach((crashType) => {
          if (!lane.score) {
            scoreOverview[crashType as keyof Score]!["null"]++;
          } else {
            Object.keys(lane.score).forEach((scoreKey) => {
              const riskFactor =
                lane.score![scoreKey as keyof Score].riskFactor;
              scoreOverview[scoreKey as keyof Score]![riskFactor]++;
            });
          }
        });
      });
    });

    Object.entries(scoreOverview).forEach(([scoreType, scores]) => {
      const totalCount = Object.values(scores).reduce(
        (sum: number, count: any) => sum + count,
        0,
      );
      data[scoreType as keyof Score] = Object.entries(scores)
        .filter(([risk, value]) => value > 0)
        .map(([risk, value]) => ({
          name: t(`score:${risk.replace("-", ".")}`),
          value: ((value / totalCount) * filteredDistance) / 1000,
          color: riskFactorColors[risk as RiskFactor],
        }));
    });
    return data;

    // eslint-disable-next-line
  }, [filteredWays]);
};
